import { Controller } from "@hotwired/stimulus";
import { Turbo } from "@hotwired/turbo-rails";

/**
 * Compose Form Controller
 *
 * Orchestrates the staff compose form:
 * - Validates form fields (recipients + message required)
 * - Manages send button enabled/disabled/loading states
 * - Calls check_existing endpoint before creating
 * - Handles dedup choice dialog (open existing or start new)
 * - Supports force_new resubmit
 */
export default class extends Controller {
  static targets = [
    "form",
    "recipientSelect",
    "aboutSelect",
    "conversableType",
    "subject",
    "messageContent",
    "sendButton",
    "dedupDialog",
    "openExistingLink",
  ];

  static values = {
    checkExistingUrl: String,
    createUrl: String,
  };

  declare readonly formTarget: HTMLFormElement;
  declare readonly hasFormTarget: boolean;
  declare readonly recipientSelectTarget: HTMLSelectElement;
  declare readonly hasRecipientSelectTarget: boolean;
  declare readonly aboutSelectTarget: HTMLSelectElement;
  declare readonly hasAboutSelectTarget: boolean;
  declare readonly conversableTypeTarget: HTMLInputElement;
  declare readonly hasConversableTypeTarget: boolean;
  declare readonly subjectTarget: HTMLInputElement;
  declare readonly hasSubjectTarget: boolean;
  declare readonly messageContentTarget: HTMLTextAreaElement;
  declare readonly hasMessageContentTarget: boolean;
  declare readonly sendButtonTarget: HTMLButtonElement;
  declare readonly hasSendButtonTarget: boolean;
  declare readonly dedupDialogTarget: HTMLElement;
  declare readonly hasDedupDialogTarget: boolean;
  declare readonly openExistingLinkTarget: HTMLAnchorElement;
  declare readonly hasOpenExistingLinkTarget: boolean;

  declare readonly checkExistingUrlValue: string;
  declare readonly createUrlValue: string;

  private forceNew: boolean = false;

  recipientsChanged() {
    this.hideDedupDialog();
  }

  aboutChanged() {
    if (!this.hasConversableTypeTarget) return;

    this.conversableTypeTarget.value = this.hasAboutSelectTarget &&
      this.aboutSelectTarget.value
      ? "Neighbor"
      : "";
  }

  validateForm() {
    if (this.hasSendButtonTarget) {
      this.sendButtonTarget.disabled = false;
    }
  }

  async submit(event?: Event) {
    event?.preventDefault();

    if (!this.isFormValid()) return;

    // If force_new is set, skip dedup check
    if (this.forceNew) {
      await this.createConversation(true);
      return;
    }

    this.setLoading(true);

    try {
      const recipientIds = this.getSelectedRecipientIds();
      const response = await fetch(this.checkExistingUrlValue, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "Accept": "application/json",
          "X-CSRF-Token": this.csrfToken,
        },
        body: JSON.stringify({ recipient_ids: recipientIds }),
      });

      const data = await response.json();

      if (data.exists) {
        this.showDedupDialog(data.conversation_url);
        this.setLoading(false);
      } else {
        await this.createConversation(false);
      }
    } catch (error) {
      console.error("Check existing failed:", error);
      // On error, proceed with creation
      await this.createConversation(false);
    }
  }

  startNew() {
    this.forceNew = true;
    this.hideDedupDialog();
    this.submit();
  }

  private async createConversation(forceNew: boolean) {
    this.setLoading(true);

    const formData = this.hasFormTarget ? new FormData(this.formTarget) : new FormData();
    const recipientIds = this.getSelectedRecipientIds();
    formData.delete("recipient_ids[]");
    recipientIds.forEach((id) => formData.append("recipient_ids[]", id));

    if (this.hasMessageContentTarget && this.messageContentTarget.value.trim()) {
      formData.set("message_content", this.messageContentTarget.value.trim());
    }

    if (this.hasSubjectTarget && this.subjectTarget.value.trim()) {
      formData.set("subject", this.subjectTarget.value.trim());
    } else {
      formData.delete("subject");
    }

    // Handle conversable (About field)
    if (this.hasAboutSelectTarget && this.aboutSelectTarget.value) {
      const conversableType = this.hasConversableTypeTarget &&
        this.conversableTypeTarget.value
        ? this.conversableTypeTarget.value
        : "Neighbor";
      formData.set("conversable_type", conversableType);
      formData.set("conversable_id", this.aboutSelectTarget.value);
    } else {
      formData.delete("conversable_type");
      formData.delete("conversable_id");
    }

    if (forceNew) {
      formData.set("force_new", "true");
    }

    try {
      const response = await fetch(this.createUrlValue, {
        method: "POST",
        headers: {
          "Accept": "text/vnd.turbo-stream.html, text/html",
          "X-CSRF-Token": this.csrfToken,
        },
        body: formData,
      });

      const html = await response.text();

      if (response.ok) {
        // Process turbo stream response (contains redirect)
        Turbo.renderStreamMessage(html);
      } else {
        // Error response - also process turbo stream (flash message)
        Turbo.renderStreamMessage(html);
        this.setLoading(false);
      }
    } catch (error) {
      console.error("Create conversation failed:", error);
      this.setLoading(false);
    }
  }

  private showDedupDialog(conversationUrl: string) {
    if (this.hasDedupDialogTarget) {
      this.dedupDialogTarget.classList.remove("hidden");
      if (this.hasOpenExistingLinkTarget) {
        this.openExistingLinkTarget.href = conversationUrl;
      }
    }
  }

  private hideDedupDialog() {
    if (this.hasDedupDialogTarget) {
      this.dedupDialogTarget.classList.add("hidden");
    }
    this.forceNew = false;
  }

  private isFormValid(): boolean {
    const hasRecipients = this.getSelectedRecipientIds().length > 0;
    const hasMessage = this.hasMessageContentTarget &&
      this.messageContentTarget.value.trim().length > 0;
    return hasRecipients && hasMessage;
  }

  private getSelectedRecipientIds(): string[] {
    if (!this.hasRecipientSelectTarget) return [];

    const select = this.recipientSelectTarget;
    const selectedOptions = Array.from(select.selectedOptions || select.options)
      .filter((opt) => opt.value && opt.selected)
      .map((opt) => opt.value);

    // Also check TomSelect's internal value
    const tomSelectInstance = (select as any).tomselect;
    if (tomSelectInstance) {
      const items = tomSelectInstance.items || [];
      return items.filter((id: string) => id !== "");
    }

    return selectedOptions;
  }

  private setLoading(loading: boolean) {
    if (this.hasSendButtonTarget) {
      if (loading) {
        this.sendButtonTarget.classList.add("loading");
      } else {
        this.sendButtonTarget.classList.remove("loading");
      }
    }
  }

  private get csrfToken(): string {
    const meta = document.querySelector('meta[name="csrf-token"]');
    return meta ? meta.getAttribute("content") || "" : "";
  }
}
